import { CSSProperties } from "react";

import logo from "assets/logo-docttorpay-horizontal.svg";
import { PaymentFeeOwner, PaymentMethod } from "types/enums";
import { SimulatorParcel } from "types/quotes";
import { formatAsPercent, formatPrice } from "utils/formats";

interface PDFSimulatorParcelsProps {
  method: PaymentMethod;
  simulatorParcels: SimulatorParcel[];
  company: { value: number; label: string };
  value: string | { value: number; label: string };
  currentPaymentFeeOwner: PaymentFeeOwner | null;
  priceTable: { value: number; label: string } | null;
}

const GREEN = "#16a34a";
const BORDER = "#e5e7eb";
const TEXT = "#1f2937";
const MUTED = "#6b7280";

const th: CSSProperties = {
  background: GREEN,
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  padding: "8px 6px",
  textAlign: "right",
  borderBottom: `1px solid ${GREEN}`,
};
const thLeft: CSSProperties = { ...th, textAlign: "left" };

const td: CSSProperties = {
  padding: "7px 6px",
  fontSize: "11px",
  color: TEXT,
  textAlign: "right",
  borderBottom: `1px solid ${BORDER}`,
  whiteSpace: "nowrap",
};
const tdLabel: CSSProperties = {
  ...td,
  textAlign: "left",
  fontWeight: 700,
};

const PDFSimulatorParcels = ({
  method,
  simulatorParcels,
  company,
  value,
  priceTable = null,
  currentPaymentFeeOwner = null,
}: PDFSimulatorParcelsProps) => {
  const isCompanyFee = currentPaymentFeeOwner === PaymentFeeOwner.Estabelecimento;
  const totalValue = typeof value === "object" ? value.value : value;
  const title = priceTable ? priceTable.label : company ? company.label : "";

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
        color: TEXT,
        padding: "4px",
      }}
    >
      {/* Cabeçalho */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          borderBottom: `2px solid ${GREEN}`,
          paddingBottom: "12px",
          marginBottom: "18px",
        }}
      >
        <img src={logo} alt="DocttorPay" style={{ height: "34px" }} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, lineHeight: 1.2 }}>
            Simulação de Parcelamento
          </div>
          {!!title && (
            <div style={{ fontSize: "13px", color: MUTED, marginTop: "2px" }}>
              {title}
            </div>
          )}
        </div>
      </header>

      {/* Resumo */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            background: "#f0fdf4",
            border: `1px solid ${BORDER}`,
            borderRadius: "999px",
            padding: "5px 12px",
          }}
        >
          Valor total:{" "}
          <b style={{ color: GREEN }}>R$ {totalValue}</b>
        </span>
        {currentPaymentFeeOwner && (
          <span
            style={{
              fontSize: "12px",
              background: "#f9fafb",
              border: `1px solid ${BORDER}`,
              borderRadius: "999px",
              padding: "5px 12px",
            }}
          >
            Taxa assumida pelo{" "}
            <b>{PaymentFeeOwner[currentPaymentFeeOwner]}</b>
          </span>
        )}
      </div>

      {/* Tabela de parcelas — quebra por linha, header repete a cada página */}
      <table
        style={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "collapse",
          border: `1px solid ${BORDER}`,
        }}
      >
        <thead style={{ display: "table-header-group" }}>
          <tr>
            <th style={thLeft}>Parcelas</th>
            <th style={th}>Valor da parcela</th>
            <th style={th}>CET</th>
            <th style={th}>Total</th>
            {isCompanyFee && <th style={th}>Compensação</th>}
          </tr>
        </thead>
        <tbody>
          {simulatorParcels?.map((parcel, index) => (
            <tr
              key={index}
              style={{
                breakInside: "avoid",
                background: index % 2 === 1 ? "#f9fafb" : "#ffffff",
              }}
            >
              <td style={tdLabel}>{parcel.parcelQtd}×</td>
              <td style={td}>{formatPrice(parcel.parcelValue)}</td>
              <td style={{ ...td, color: MUTED }}>
                {parcel.cetCost != null ? formatAsPercent(parcel.cetCost) : "—"}
              </td>
              <td style={{ ...td, fontWeight: 700 }}>
                {formatPrice(parcel.totalValue)}
              </td>
              {isCompanyFee && (
                <td style={{ ...td, color: GREEN, fontWeight: 700 }}>
                  {formatPrice(parcel.compensationValue)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Observações (financiamento) — em fluxo normal, sem sobreposição */}
      {method === PaymentMethod.Financing && (
        <footer
          style={{
            marginTop: "20px",
            paddingTop: "10px",
            borderTop: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: "10px",
            lineHeight: 1.5,
          }}
        >
          <div>
            *Informações de uso interno — este relatório não deve ser enviado ao
            usuário final.
          </div>
          <div>*Valores de referência. Crédito sujeito à aprovação.</div>
          <div>
            *As taxas podem variar de 2% a.m. até 5% a.m. — dependem da quantidade
            de parcelas, score do solicitante, valor do seguro, local de moradia e
            trabalho do solicitante e tarifa de IOF conforme legislação vigente.
          </div>
          <div>
            *Não envie print da simulação ao paciente; os valores são de referência
            e podem mudar conforme o perfil financeiro. Use apenas como base,
            informando que a parcela é &ldquo;a partir de&rdquo; o valor simulado.
          </div>
        </footer>
      )}
    </div>
  );
};

export default PDFSimulatorParcels;
